/**
 * Home's building blocks: the two green action cards, the two lighter tiles
 * beneath them, the three assurances and the How It Works bar.
 *
 * Each is sized by `homeLayout`, from the column the screen actually has, so
 * a 320pt phone, a tablet and a desktop browser each get sizes that fit
 * rather than a phone layout stretched or squeezed.
 */

import { memo, useCallback, useState, type ReactNode } from 'react';
import { Pressable, StyleSheet, View, type LayoutChangeEvent } from 'react-native';

import { colors, fontSize, radius, shadow, sizing, space } from '../../theme';
import { Text } from '../common/Text';
import { useLanguage } from '../../core/i18n/LanguageProvider';
import { BookArt, LeafArt } from './HomeArt';

/**
 * Widths per point of font size, measured in the web build's system font —
 * the widest of the three platforms' for these words — at the smallest size
 * each is set in. Measured there because Apple's system font is a wider cut
 * below 20pt: taken at 20pt, the figures came out 4-7% short and the words
 * wrapped.
 */
export const WIDTH_PER_PT = {
  /** The hero title's longer line, bold, at 17pt. */
  heroTitle: 9.57,
  /** "Support a Meal", the longer action title, bold, at 17pt. */
  actionTitle: 7.22,
  /** "New Features", the longer tile title, semibold, at 15pt. */
  tileTitle: 6.56,
  /** "Delivered with Care", the longest assurance title, semibold, at 13pt. */
  assuranceTitle: 9.5,
} as const;

/**
 * The width, per point of font size, that breaks both green cards'
 * descriptions where the mockup does: "Get a nutritious meal / delivered to
 * your doorstep." and "Contribute towards meals / for those in need." It has
 * to hold "delivered to your doorstep." (12.29 at 15pt, 12.55 at 13pt) and
 * refuse "Contribute towards meals for" (13.24 and 13.51), so it sits between
 * them at both sizes, with room either side for a slightly different font.
 */
const DESCRIPTION_WIDTH_PER_PT = 12.9;
/** A tenth held back, so a slightly wider font still fits. */
const FIT = 0.9;

/** The tiles' outline and padding, each side. */
export const TILE_BORDER = 1;
export const TILE_PADDING = space.md;
const TILE_ARROW = 28;
/** The How It Works bar's height at its natural size. */
const GUIDE_HEIGHT = 52;

const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

export interface HomeLayout {
  /** The column Home lays out in: the window, capped, less the gutters. */
  column: number;
  /** A short window: the green cards must still be above the fold. */
  compact: boolean;
  hero: { padding: number; art: number; title: number; subtitle: number };
  action: {
    padding: number;
    /** Top and bottom; tighter than the sides when Home is fitted to the screen. */
    paddingVertical: number;
    art: number;
    title: number;
    description: number;
    /** Breaks the description into the mockup's two lines. */
    descriptionWidth: number;
    chevron: number;
  };
  tile: {
    width: number;
    across: boolean;
    art: number;
    /** Top and bottom inside the outline; the sides stay TILE_PADDING. */
    paddingVertical: number;
    title: number;
    description: number;
  };
  assurance: { blob: number; padding: number; title: number; description: number };
  /** The How It Works bar. */
  guide: { height: number };
  /** The gaps between Home's blocks. */
  gap: { cards: number; blocks: number; tiles: number; assurances: number };
}

/**
 * Every size on Home, from the window.
 *
 * Sizes follow the column, except in a short window — a phone turned on its
 * side — where they take a phone's, so the first green card is on screen
 * rather than the hero filling it.
 *
 * `squeeze`, from 0 to 1, tightens Home's vertical space so it fits a tall
 * phone without scrolling (HomeScreen measures how much it needs). It takes
 * space — padding, drawings, gaps — and a point or two from the sentences
 * under the titles. Titles never change, and every size the words are fitted
 * to is worked out from the unsqueezed layout.
 */
export function homeLayout(width: number, height: number, squeeze = 0): HomeLayout {
  const column = Math.min(width, sizing.donorMaxWidth) - 2 * space.lg;
  const short = height < sizing.shortWindowHeight;
  const compact = height < 700;
  // The column the sizes are chosen for.
  const sized = short ? Math.min(column, 380) : column;
  const small = sized < 330;
  // A phone held upright is at most about 480pt wide (440 for the largest
  // iPhone); only past that do the cards and type grow towards a tablet's.
  const phone = sized < 448;
  const tighten = (natural: number, tightest: number) =>
    Math.round(natural + (tightest - natural) * squeeze);

  // The words stop where the drawing starts; the drawing takes the card's
  // right padding and reaches to its edge.
  const heroPadding = phone ? space.lg : space.xl;
  const heroArt = Math.round(
    clamp(sized * (phone ? 0.3 : 0.34), 90, 176) * (compact ? 0.9 : 1),
  );
  const heroWords = column - heroPadding - heroArt - space.sm;
  const heroTitle = clamp(
    Math.floor((heroWords * FIT) / WIDTH_PER_PT.heroTitle),
    fontSize.lg - 1,
    short ? fontSize['2xl'] : fontSize['4xl'],
  );

  // The headline stays the largest words on the screen.
  const actionTitle = Math.min(
    small ? fontSize.lg - 1 : phone ? fontSize.xl : fontSize['2xl'],
    heroTitle,
  );
  const actionPadding = phone ? space.lg : space.xl;
  const actionArt = small ? 56 : phone ? 62 : 88;

  const tileWidth = (column - space.md) / 2;
  const tileTitle = phone ? fontSize.base : fontSize.lg;
  const tileArt = phone ? 40 : 56;
  // Drawing beside the words only where the title still fits beside it;
  // otherwise the drawing sits above the words.
  const across =
    (tileWidth - 2 * TILE_BORDER - 2 * TILE_PADDING - tileArt - space.sm) * FIT >=
    tileTitle * WIDTH_PER_PT.tileTitle;

  const assuranceColumn = column / 3;
  const assurancePadding = column < 340 ? space.xxs : column < 480 ? space.xs : space.sm;
  // All three titles on one line wherever "Delivered with Care" fits at the
  // smallest size (from about 415pt wide); narrower, only it wraps, and the
  // descriptions still line up beneath (see useTallestTitle).
  const assuranceTitle = clamp(
    Math.floor((assuranceColumn - 2 * assurancePadding) / WIDTH_PER_PT.assuranceTitle),
    fontSize.sm,
    fontSize.md,
  );
  const assuranceBlob = Math.round(clamp(assuranceColumn * 0.5, 56, 84) * (compact ? 0.9 : 1));
  const description = small ? fontSize.sm : fontSize.base;
  const heroSubtitle = small ? fontSize.sm : phone ? fontSize.base : fontSize.md;

  return {
    column,
    compact,
    hero: {
      padding: tighten(heroPadding, space.sm),
      art: tighten(heroArt, heroArt * 0.75),
      title: heroTitle,
      subtitle: tighten(heroSubtitle, heroSubtitle - 2),
    },
    action: {
      padding: actionPadding,
      paddingVertical: tighten(actionPadding, space.sm),
      art: tighten(actionArt, actionArt * 0.8),
      title: actionTitle,
      description: tighten(description, description - 1),
      // From the unsqueezed size, so the sentence breaks where it always does.
      descriptionWidth: Math.round(description * DESCRIPTION_WIDTH_PER_PT),
      chevron: phone ? 38 : 44,
    },
    tile: {
      width: tileWidth,
      across,
      art: tighten(tileArt, tileArt * 0.75),
      paddingVertical: tighten(TILE_PADDING, space.sm - space.xxs),
      title: tileTitle,
      description: tighten(fontSize.sm, fontSize.sm - 1),
    },
    assurance: {
      blob: tighten(assuranceBlob, assuranceBlob * 0.6),
      padding: assurancePadding,
      title: assuranceTitle,
      // Three short lines side by side: small and set close (the `detail`
      // role) on a phone, so each stays two or three lines rather than four.
      description: phone ? fontSize.xs : fontSize.sm,
    },
    // Never below a comfortable touch target.
    guide: { height: tighten(GUIDE_HEIGHT, sizing.touchTarget) },
    gap: {
      cards: tighten(compact ? space.sm : space.md, space.xs + space.xxs),
      blocks: tighten(space.md, space.xs + space.xxs),
      // Room for the Coming Soon tag, which rises a tile's-edge above it.
      tiles: tighten(space.lg, space.sm + space.xxs),
      assurances: tighten(space.lg, space.xs),
    },
  };
}

/**
 * A bold arrow drawn as two strokes, pointing the way the language reads.
 *
 * Strokes rather than a bordered square: a border is a left or a right, and
 * Android swaps those in Urdu while iOS and the web do not.
 */
export function Chevron({ size, color }: { size: number; color: string }) {
  const { isRTL } = useLanguage();
  const stroke = Math.max(2, Math.round(size * 0.16));
  const arm = size * 0.62;
  // Each stroke's centre sits this far above or below the middle, so the two
  // meet at the point.
  const lift = arm * 0.354;
  const bar = {
    width: arm,
    height: stroke,
    borderRadius: stroke,
    backgroundColor: color,
  };
  return (
    <View
      style={[styles.chevron, { width: size, height: size }, isRTL && styles.flipped]}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      testID="icon-arrow"
    >
      <View style={[styles.stroke, bar, { transform: [{ translateY: -lift }, { rotate: '45deg' }] }]} />
      <View style={[styles.stroke, bar, { transform: [{ translateY: lift }, { rotate: '-45deg' }] }]} />
    </View>
  );
}

/** A round button face with an arrow on it. */
function Arrow({ size, face, ink }: { size: number; face: string; ink: string }) {
  return (
    <View style={[styles.arrow, { width: size, height: size, borderRadius: size / 2, backgroundColor: face }]}>
      <Chevron size={Math.round(size * 0.42)} color={ink} />
    </View>
  );
}

export interface ActionCardProps {
  title: string;
  description: string;
  art: (size: number) => ReactNode;
  layout: HomeLayout['action'];
  onPress: () => void;
  disabled?: boolean;
  accessibilityHint?: string;
  testID: string;
}

/** A deep green card: one of the two things Home is for. */
export const ActionCard = memo(function ActionCard({
  title,
  description,
  art,
  layout,
  onPress,
  disabled = false,
  accessibilityHint,
  testID,
}: ActionCardProps) {
  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={`${title}. ${description}`}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ disabled }}
      aria-disabled={disabled}
      style={({ pressed }) => [
        styles.action,
        { padding: layout.padding, paddingVertical: layout.paddingVertical },
        pressed && styles.actionPressed,
        disabled && styles.disabled,
      ]}
    >
      {art(layout.art)}
      <View style={styles.actionText}>
        <Text tone="onBrand" variant="screenTitle" size={layout.title}>
          {title}
        </Text>
        <Text
          tone="onBrand"
          variant="body"
          size={layout.description}
          style={{ maxWidth: layout.descriptionWidth }}
        >
          {description}
        </Text>
      </View>
      <Arrow size={layout.chevron} face={colors.bg.surface} ink={colors.text.brand} />
    </Pressable>
  );
});

export interface TileCardProps {
  title: string;
  description: string;
  art: (size: number) => ReactNode;
  layout: HomeLayout['tile'];
  tint: string;
  onPress: () => void;
  /** Something to pin to the top edge, e.g. a Coming Soon tag. */
  badge?: ReactNode;
  accessibilityHint?: string;
  testID: string;
}

/**
 * A lighter card for the supporting actions, two to a row.
 *
 * Titles at the top and arrows at the bottom, whatever each description's
 * length, so the two tiles line up with each other.
 */
export const TileCard = memo(function TileCard({
  title,
  description,
  art,
  layout,
  tint,
  onPress,
  badge,
  accessibilityHint,
  testID,
}: TileCardProps) {
  const arrow = <Arrow size={TILE_ARROW} face={colors.status.success.border} ink={colors.text.brand} />;
  const words = (
    <View style={styles.tileWords}>
      <Text variant="cardTitle" tone="brand" size={layout.title}>
        {title}
      </Text>
      <Text variant="caption" tone="secondary" size={layout.description}>
        {description}
      </Text>
    </View>
  );
  return (
    <View style={styles.tileSlot}>
      <Pressable
        testID={testID}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`${title}. ${description}`}
        accessibilityHint={accessibilityHint}
        style={({ pressed }) => [
          styles.tile,
          { backgroundColor: tint, paddingVertical: layout.paddingVertical },
          pressed && styles.tilePressed,
        ]}
      >
        {layout.across ? (
          <View style={styles.tileAcross}>
            <View style={styles.tileArt}>{art(layout.art)}</View>
            <View style={styles.tileColumn}>
              {words}
              <View style={styles.tileArrowEnd}>{arrow}</View>
            </View>
          </View>
        ) : (
          <>
            <View style={styles.tileTop}>
              {art(layout.art)}
              {arrow}
            </View>
            {words}
          </>
        )}
      </Pressable>
      {badge}
    </View>
  );
});

/**
 * The tallest of the three assurance titles, so each description starts on
 * the same line whichever titles wrap. Measured, not guessed: a title wraps
 * by font, language and width, and none of those is known here.
 */
export function useTallestTitle(resetKey: string) {
  const [heights, setHeights] = useState<{ key: string; values: number[] }>({
    key: resetKey,
    values: [],
  });
  const onLayout = useCallback(
    (index: number) => (event: LayoutChangeEvent) => {
      const measured = Math.ceil(event.nativeEvent.layout.height);
      setHeights((current) => {
        const values = current.key === resetKey ? [...current.values] : [];
        if (values[index] === measured) return current;
        values[index] = measured;
        return { key: resetKey, values };
      });
    },
    [resetKey],
  );
  const measured = heights.key === resetKey ? heights.values.filter(Boolean) : [];
  return { minHeight: measured.length ? Math.max(...measured) : undefined, onLayout };
}

/** One promise, with its drawing, in a third of the row. */
export function Assurance({
  art,
  title,
  description,
  layout,
  titleHeight,
  onTitleLayout,
}: {
  art: ReactNode;
  title: string;
  description: string;
  layout: HomeLayout['assurance'];
  titleHeight: number | undefined;
  onTitleLayout: (event: LayoutChangeEvent) => void;
}) {
  return (
    <View
      style={[styles.assurance, { paddingHorizontal: layout.padding }]}
      accessible
      accessibilityLabel={`${title}. ${description}`}
    >
      {art}
      {/* The title's own height is measured inside a box that holds the
          tallest one's, so the measurement is never of the box. */}
      <View style={[styles.assuranceTitle, { minHeight: titleHeight }]} testID="assurance-title">
        <Text variant="cardTitle" tone="brand" align="center" size={layout.title} onLayout={onTitleLayout}>
          {title}
        </Text>
      </View>
      <Text variant="detail" tone="secondary" align="center" size={layout.description}>
        {description}
      </Text>
    </View>
  );
}

/** The line between two assurances. */
export function AssuranceRule() {
  return <View style={styles.rule} />;
}

/** How It Works: a quiet outlined bar with a book, closing the screen. */
export const GuideBar = memo(function GuideBar({
  label,
  onPress,
  height = GUIDE_HEIGHT,
  accessibilityHint,
  testID,
}: {
  label: string;
  onPress: () => void;
  height?: number;
  accessibilityHint?: string;
  testID: string;
}) {
  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={accessibilityHint}
      style={({ pressed }) => [styles.guide, { minHeight: height }, pressed && styles.tilePressed]}
    >
      <LeafArt size={56} style={styles.guideLeafStart} />
      <LeafArt size={56} mirrored style={styles.guideLeafEnd} />
      <BookArt size={30} />
      <Text variant="sectionTitle" tone="brand">
        {label}
      </Text>
      <Chevron size={16} color={colors.text.brand} />
    </Pressable>
  );
});

const styles = StyleSheet.create({
  chevron: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  stroke: {
    position: 'absolute',
  },
  arrow: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  flipped: {
    transform: [{ scaleX: -1 }],
  },
  action: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    borderRadius: radius.xl,
    backgroundColor: colors.bg.brand,
    ...shadow('card'),
  },
  actionPressed: {
    backgroundColor: colors.action.primaryPressed,
  },
  disabled: {
    opacity: 0.55,
  },
  actionText: {
    flex: 1,
    minWidth: 0,
    gap: space.xs,
  },
  tileSlot: {
    flex: 1,
    minWidth: 0,
  },
  tile: {
    flex: 1,
    padding: TILE_PADDING,
    borderRadius: radius.lg,
    borderWidth: TILE_BORDER,
    borderColor: colors.border.default,
    ...shadow('card'),
  },
  tilePressed: {
    opacity: 0.85,
  },
  tileAcross: {
    flex: 1,
    flexDirection: 'row',
    gap: space.sm,
  },
  tileArt: {
    justifyContent: 'center',
  },
  tileColumn: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'space-between',
  },
  tileWords: {
    gap: space.xxs,
  },
  tileArrowEnd: {
    alignItems: 'flex-end',
    marginTop: space.xxs,
  },
  tileTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: space.sm,
  },
  assurance: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    gap: space.xs,
  },
  assuranceTitle: {
    marginTop: space.xs,
    justifyContent: 'center',
  },
  rule: {
    width: StyleSheet.hairlineWidth,
    alignSelf: 'stretch',
    backgroundColor: colors.border.default,
  },
  guide: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: space.md,
    paddingHorizontal: space.xl,
    borderRadius: radius.xl,
    borderWidth: 1,
    borderColor: colors.status.success.border,
    backgroundColor: colors.bg.surface,
    overflow: 'hidden',
  },
  guideLeafStart: {
    position: 'absolute',
    start: -space.sm,
    bottom: -space.md,
  },
  guideLeafEnd: {
    position: 'absolute',
    end: -space.sm,
    bottom: -space.md,
  },
});
